import { bucketDate, type Step } from './time';

// The customer app uses a few non-BCP-47 language codes.
const LOCALE_ALIASES: Record<string, string> = { dk: 'da', no: 'nb' };

export function intlLocale(locale?: string): string | undefined {
  return locale ? (LOCALE_ALIASES[locale] ?? locale) : undefined;
}

export function formatNumber(value: number, locale?: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: 0,
  }).format(value);
}

/** Axis-tick number: compact above 10k (12K, 1.2M). */
export function formatCompact(value: number, locale?: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    notation: Math.abs(value) >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatPercent(ratio: number, locale?: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(ratio);
}

/**
 * Formats a bucket key. `short` is for axis ticks, `long` for tooltips.
 * `withYear` adds the year to short month labels when a range spans years.
 */
export function formatBucket(
  key: string,
  step: Step,
  {
    locale,
    variant = 'short',
    withYear = false,
  }: { locale?: string; variant?: 'short' | 'long'; withYear?: boolean } = {},
): string {
  const options: Intl.DateTimeFormatOptions =
    step === 'year'
      ? { year: 'numeric' }
      : step === 'month'
        ? variant === 'long'
          ? { month: 'long', year: 'numeric' }
          : { month: 'short', year: withYear ? '2-digit' : undefined }
        : variant === 'long'
          ? { day: 'numeric', month: 'long', year: 'numeric' }
          : { day: 'numeric' };
  return bucketDate(key).toLocaleDateString(intlLocale(locale), {
    ...options,
    timeZone: 'UTC',
  });
}

type DurationParts = Partial<Record<'hours' | 'minutes', number>>;
type DurationFormatCtor = new (
  locale?: string,
  options?: { style: 'narrow' | 'short' },
) => { format: (d: DurationParts) => string };

/**
 * Compact duration for a headline figure, in hours at most: "34 min",
 * "5h 12m", "342h". Minutes are dropped from a day up, as noise at that
 * scale.
 */
export function formatDuration(seconds: number, locale?: string): string {
  const totalMinutes = Math.round(Math.max(seconds, 0) / 60);
  const parts: DurationParts =
    totalMinutes < 60
      ? { minutes: totalMinutes }
      : totalMinutes < 24 * 60
        ? {
            hours: Math.floor(totalMinutes / 60),
            minutes: totalMinutes % 60 || undefined,
          }
        : { hours: Math.round(totalMinutes / 60) };
  const DurationFormat = (
    Intl as unknown as { DurationFormat?: DurationFormatCtor }
  ).DurationFormat;
  if (!DurationFormat) {
    return parts.hours === undefined
      ? `${parts.minutes} min`
      : `${parts.hours}h${parts.minutes ? ` ${parts.minutes}m` : ''}`;
  }
  // Minutes alone read better spelled out ("34 min" rather than "34m").
  const style = parts.hours === undefined ? 'short' : 'narrow';
  return new DurationFormat(intlLocale(locale), { style }).format(parts);
}
